<template>
  <div 
    class="relative w-full"
    :class="[!inline ? 'md:flex-shrink-0' : '', isOpen && inlineDropdown ? 'z-50' : '']"
  >
    <!-- Dropdown Input (hidden in inline mode) -->
    <div 
      v-if="!inline"
      ref="inputRef"
      @click="toggleCalendar"
      role="button"
      tabindex="0"
      class="relative w-full md:min-w-[300px] h-[50px] px-4 flex items-center shadow-sm bg-white cursor-pointer transition-all focus:outline-none group"
      :class="[
        isOpen 
          ? (isDesktop ? (variant === 'light' ? 'border border-gray-200 rounded-3xl' : 'border border-[#2F3033] rounded-full') : 'border border-[#4ECF84] rounded-full ring-2 ring-[#4ECF84]/15') 
          : (variant === 'light' ? 'border border-gray-200 rounded-3xl hover:border-gray-400' : 'border border-[#2F3033] rounded-full hover:border-gray-900'),
        !isPlaceholder ? 'pr-20 md:pr-20' : 'pr-12'
      ]"
    >
      <div class="flex items-center w-full gap-4">
        <!-- Calendar icon — left of text -->
        <CalendarDays
          class="w-5 h-5 shrink-0 text-[#4ECF84]"
          :stroke-width="1.5"
        />
        <!-- Desktop text -->
        <span
          class="select-none flex-1 text-left truncate hidden md:block text-base font-semibold text-[#2F3033]"
          :class="['leading-normal']"
          style="font-family: 'Raleway', sans-serif;"
        >
          {{ displayText }}
        </span>
        <!-- Mobile text -->
        <span
          class="select-none flex-1 text-left truncate md:hidden text-lg font-semibold text-[#2F3033]"
          :class="['leading-tight']"
          style="font-family: 'Raleway', sans-serif;"
        >
          {{ mobileDisplayText }}
        </span>
      </div>


      <!-- Clear Button (only visible on mobile when there's a selection) -->
      <button
        v-if="!isPlaceholder"
        type="button"
        @click.stop="clearSelection"
        class="absolute right-10 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-[#4ECF84] transition-colors z-50 md:hidden"
        title="Clear selection"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>


    <!-- Calendar Dropdown -->
    <div 
      v-if="isOpen"
      :class="[
        inline
          ? 'relative bg-white mx-auto'
          : (inlineDropdown
              ? 'relative bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm z-[60] p-4 mt-2'
              : 'fixed bg-white border border-[#2F3033] rounded-[3rem] md:rounded-2xl overflow-hidden shadow-lg z-[60] p-4 left-2 right-2 bottom-24 max-h-[90vh] overflow-y-auto md:max-h-none md:overflow-visible md:left-auto md:right-auto md:bottom-auto')
      ]"
      :style="inline || inlineDropdown ? {} : dropdownStyle"
    >
      <div>

          <!-- Calendar Header -->
          <div class="flex items-center justify-between mb-4">
            <button 
              @click="previousMonth"
              class="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>

            <div class="flex-1 flex justify-around items-center">
              <h3 class="text-base md:text-sm font-semibold text-black">
                {{ formatMonthYear(currentMonth) }}
              </h3>
              <!-- Second Month Label (Desktop only) -->
              <h3 v-if="!inlineDropdown" class="text-base md:text-sm font-semibold text-black hidden md:block">
                {{ formatMonthYear(nextMonth) }}
              </h3>
            </div>

            <button 
              @click="goToNextMonth"
              class="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>

          <!-- Calendar Grid Container -->
          <div class="flex flex-col gap-8" :class="!inlineDropdown ? 'md:flex-row' : ''">
            <!-- First Month -->
            <div class="w-full">
              <div class="grid grid-cols-7 gap-2 mb-3">
                <div v-for="day in dayHeaders" :key="day" class="text-center text-xs font-semibold text-[#2F3033] py-1 opacity-50">
                  {{ day }}
                </div>
              </div>
              <div class="grid grid-cols-7 gap-2">
                <button
                  v-for="date in currentMonthDates"
                  :key="date.key"
                  @click="selectDate(date)"
                  :disabled="date.disabled"
                  :class="getDateClasses(date)"
                  class="w-full aspect-square text-sm font-semibold leading-none rounded-full transition-colors disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {{ date.day }}
                </button>
              </div>
            </div>

            <!-- Second Month (Desktop Only) -->
            <div v-if="!inlineDropdown" class="w-full hidden md:block">
              <div class="grid grid-cols-7 gap-2 mb-3">
                <div v-for="day in dayHeaders" :key="day" class="text-center text-xs font-semibold text-[#2F3033] py-1 opacity-50">
                  {{ day }}
                </div>
              </div>
              <div class="grid grid-cols-7 gap-2">
                <button
                  v-for="date in nextMonthDates"
                  :key="date.key"
                  @click="selectDate(date)"
                  :disabled="date.disabled"
                  :class="getDateClasses(date)"
                  class="w-full aspect-square text-sm leading-none rounded-full transition-colors disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {{ date.day }}
                </button>
              </div>
            </div>
          </div>
      </div>

      <!-- Apply Button (removed as per user request for simpler flow) -->
    </div>

    <!-- Backdrop to close calendar -->
    <div 
      v-if="isOpen && !inline"
      @click="closeCalendar"
      :class="inlineDropdown ? 'fixed inset-0 z-30' : 'fixed inset-0 z-40'"
    ></div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { iconRegistry } from '@/icons'

const CalendarDays = iconRegistry['calendar-days']

export default {
  name: 'DropDownCalendar',
  components: { CalendarDays },
  props: {
    modelValue: {
      type: Object,
      default: null
    },
    resetCalendarKey: {
      type: Number,
      default: 0
    },
    // When true, render the calendar inline (used inside accordions on mobile)
    inline: {
      type: Boolean,
      default: false
    },
    // External display text to override internal state
    externalDisplayText: {
      type: String,
      default: null
    },
    inlineDropdown: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'dark',
      validator: (v) => ['dark', 'light'].includes(v)
    }
  },
  emits: ['dateSelected', 'todaySelected', 'notSureSelected', 'weekendSelected', 'tempDateChanged', 'dropdown-open', 'update:modelValue'],
  setup(props, { emit }) {
    const isOpen = ref(props.inline ? true : false)
    const currentMonth = ref(new Date())
    const tempStartDate = ref(props.modelValue?.startDate || null)
    const tempEndDate = ref(props.modelValue?.endDate || null)
    const startDate = ref(props.modelValue?.startDate || null)
    const endDate = ref(props.modelValue?.endDate || null)
    const inputRef = ref(null)
    const quickSelection = ref(null) // 'today' | 'weekend' | 'not-sure' | null
    const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0)

    const isDesktop = computed(() => windowWidth.value >= 768)

    const dayHeaders = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

    // Computed properties

    const getDaysCount = (start, end) => {
      const msPerDay = 1000 * 60 * 60 * 24
      // +1 because if start = 01/01 and end = 02/01, it's 2 days
      const days = Math.round((end.getTime() - start.getTime()) / msPerDay) + 1
      return `(${days} day${days > 1 ? 's' : ''})`
    }

    const displayText = computed(() => {
      // Use external display text if provided
      if (props.externalDisplayText) {
        return props.externalDisplayText
      }
      
      if (quickSelection.value === 'not-sure') {
        return 'Not sure yet'
      }
      
      // Show real-time selection (temp dates) when calendar is open and dates are being selected
      if (isOpen.value && tempStartDate.value && tempEndDate.value) {
        const start = formatDate(tempStartDate.value)
        const end = formatDate(tempEndDate.value)
        return `${start} - ${end} ${getDaysCount(tempStartDate.value, tempEndDate.value)}`
      } else if (isOpen.value && tempStartDate.value) {
        return formatDate(tempStartDate.value)
      }
      
      // Show confirmed dates when calendar is closed
      if (startDate.value && endDate.value) {
        const start = formatDate(startDate.value)
        const end = formatDate(endDate.value)
        return `${start} - ${end} ${getDaysCount(startDate.value, endDate.value)}`
      } else if (startDate.value) {
        return formatDate(startDate.value)
      }
      return 'Select dates'
    })

    const mobileDisplayText = computed(() => {
      if (quickSelection.value === 'not-sure') {
        return 'Not sure yet'
      }
      
      // Show real-time selection (temp dates) when calendar is open and dates are being selected
      if (isOpen.value && tempStartDate.value && tempEndDate.value) {
        const start = formatDate(tempStartDate.value)
        const end = formatDate(tempEndDate.value)
        return `${start} - ${end} ${getDaysCount(tempStartDate.value, tempEndDate.value)}`
      } else if (isOpen.value && tempStartDate.value) {
        return formatDate(tempStartDate.value)
      }
      
      // Show confirmed dates when calendar is closed
      if (startDate.value && endDate.value) {
        const start = formatDate(startDate.value)
        const end = formatDate(endDate.value)
        return `${start} - ${end} ${getDaysCount(startDate.value, endDate.value)}`
      } else if (startDate.value) {
        return formatDate(startDate.value)
      }
      return 'When are you traveling?'
    })

    // True when showing placeholder text
    const isPlaceholder = computed(() => {
      // If external display text is provided, check if it's a placeholder
      if (props.externalDisplayText) {
        return props.externalDisplayText === 'Select dates'
      }
      
      // Not a placeholder if we have temp dates (real-time selection) or confirmed dates
      if (tempStartDate.value || startDate.value || quickSelection.value === 'not-sure') {
        return false
      }
      
      return true
    })

    // Quick-select button labels
    const todayLabel = computed(() => formatDate(new Date()))

    const getUpcomingWeekendRange = () => {
      // Nearest Friday through Sunday
      const now = new Date()
      now.setHours(0, 0, 0, 0)
      const day = now.getDay() // 0=Sun ... 5=Fri, 6=Sat
      const daysUntilFriday = (5 - day + 7) % 7
      const friday = new Date(now)
      friday.setDate(now.getDate() + daysUntilFriday)
      const sunday = new Date(friday)
      sunday.setDate(friday.getDate() + 2)
      return { friday, sunday }
    }

    const formatRangeLabel = (start, end) => {
      const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
      const startStr = start.toLocaleDateString('en-GB', { day: 'numeric' })
      const endStr = end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      if (sameMonth) {
        // e.g., 3-5 Oct
        return `${startStr}-${endStr}`
      }
      // e.g., 29 Sep - 1 Oct
      const startFull = start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      const endFull = end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      return `${startFull} - ${endFull}`
    }

    const weekendLabel = computed(() => {
      const { friday, sunday } = getUpcomingWeekendRange()
      return formatRangeLabel(friday, sunday)
    })

    const dropdownStyle = computed(() => {
      if (!inputRef.value || !isOpen.value) return {}
      const rect = inputRef.value.getBoundingClientRect()

      // Inline mode: render inside normal flow, no positioning
      if (props.inline) {
        return {}
      }

      // Mobile: render as a bottom sheet so it's always interactable
      if (!isDesktop) {
        return {
          left: '0.5rem',            // left-2
          right: '0.5rem',           // right-2
          bottom: '6rem',            // bottom-24, keep above mobile nav bar
          top: 'auto',
          width: 'auto',
          maxHeight: '90vh',
          overflowY: 'auto'
        }
      }

      // Desktop: anchored dropdown near input with viewport bounds checking
      const minDesktopWidth = 560
      const width = Math.max(rect.width, minDesktopWidth)
      const viewportWidth = window.innerWidth
      
      // Calculate left position, ensuring dropdown stays within viewport
      let leftPosition = rect.left
      
      // If dropdown would extend beyond right edge, align it to the right edge of viewport
      if (leftPosition + width > viewportWidth - 20) {
        leftPosition = viewportWidth - width - 20
      }
      
      // Ensure dropdown doesn't go beyond left edge
      if (leftPosition < 20) {
        leftPosition = 20
      }
      
      const style = {
        top: `${rect.bottom + 14}px`,
        left: `${leftPosition}px`,
        width: `${width}px`,
        minWidth: `${minDesktopWidth}px`
      }
      return style
    })

    // Generate calendar dates for a month
    const generateMonthDates = (month) => {
      const dates = []
      const firstDay = new Date(month.getFullYear(), month.getMonth(), 1)
      const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0)
      const startDate = new Date(firstDay)
      startDate.setDate(startDate.getDate() - firstDay.getDay())

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      for (let i = 0; i < 42; i++) {
        const date = new Date(startDate)
        date.setDate(startDate.getDate() + i)
        
        const isCurrentMonth = date.getMonth() === month.getMonth()
        const isPast = date < today
        
        dates.push({
          date: new Date(date),
          day: date.getDate(),
          isCurrentMonth,
          disabled: !isCurrentMonth || isPast,
          key: `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
        })
      }

      return dates
    }

    const currentMonthDates = computed(() => generateMonthDates(currentMonth.value))
    
    const nextMonth = computed(() => {
      const next = new Date(currentMonth.value)
      next.setDate(1) // Prevent month rollover issues (e.g. Jan 31 -> Feb 28/Mar 3)
      next.setMonth(next.getMonth() + 1)
      return next
    })
    
    const nextMonthDates = computed(() => generateMonthDates(nextMonth.value))

    // Methods
    const toggleCalendar = () => {
      isOpen.value = !isOpen.value
      if (isOpen.value) {
        tempStartDate.value = startDate.value
        tempEndDate.value = endDate.value
        // Emit initial temp dates when opening
        emit('tempDateChanged', {
          tempStartDate: tempStartDate.value,
          tempEndDate: tempEndDate.value
        })
        emit('dropdown-open')
      }
    }

    const closeCalendar = () => {
      isOpen.value = false
      tempStartDate.value = null
      tempEndDate.value = null
    }

    const selectDate = (dateObj) => {
      if (dateObj.disabled) return

      const selectedDate = dateObj.date
      // Clear quick selection state when manually picking dates
      quickSelection.value = null

      if (!tempStartDate.value || (tempStartDate.value && tempEndDate.value)) {
        // Start new selection
        tempStartDate.value = selectedDate
        tempEndDate.value = null
      } else if (selectedDate < tempStartDate.value) {
        // Selected date is before start date, make it the new start
        tempStartDate.value = selectedDate
        tempEndDate.value = null
      } else {
        // Set as end date
        tempEndDate.value = selectedDate
        
        // Auto-apply if inline mode is active OR if on desktop (since Apply button is removed)
        const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768
        if (props.inline || isDesktop) {
          applySelection()
        }
      }
      
      // Emit temp date change for real-time updates
      emit('tempDateChanged', {
        tempStartDate: tempStartDate.value,
        tempEndDate: tempEndDate.value
      })
    }

    const applySelection = () => {
      if (tempStartDate.value) {
        startDate.value = tempStartDate.value
        endDate.value = tempEndDate.value
        
        emit('dateSelected', {
          startDate: startDate.value,
          endDate: endDate.value
        })
        emit('update:modelValue', {
          startDate: startDate.value,
          endDate: endDate.value
        })
      }
      // Keep open in inline mode; otherwise close the dropdown
      if (!props.inline) closeCalendar()
    }

    const onSelectToday = () => {
      // Set selection to today so the preview updates
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      startDate.value = today
      endDate.value = null
      tempStartDate.value = today
      tempEndDate.value = null
      quickSelection.value = 'today'
      emit('todaySelected')
      if (!props.inline) closeCalendar()
    }

    const onSelectNotSure = () => {
      // Clear any existing date selections
      startDate.value = null
      endDate.value = null
      tempStartDate.value = null
      tempEndDate.value = null
      quickSelection.value = 'not-sure'
      emit('notSureSelected')
      if (!props.inline) closeCalendar()
    }

    const onSelectWeekend = () => {
      // Calculate upcoming weekend (Friday-Sunday)
      const { friday, sunday } = getUpcomingWeekendRange()
      // Update local state so preview updates immediately
      startDate.value = friday
      endDate.value = sunday
      tempStartDate.value = friday
      tempEndDate.value = sunday
      quickSelection.value = 'weekend'

      // Emit a full selection so parent logic works out-of-the-box
      emit('dateSelected', {
        startDate: friday,
        endDate: sunday
      })
      emit('weekendSelected')
      if (!props.inline) closeCalendar()
    }

    const getDateClasses = (dateObj) => {
      const classes = []
      
      if (dateObj.disabled) {
        classes.push('text-gray-300 cursor-not-allowed')
      } else if (!dateObj.isCurrentMonth) {
        classes.push('text-gray-300')
      } else {
        classes.push('text-[#2F3033] hover:bg-gray-100')
      }

      // Check if this date is selected
      if (tempStartDate.value && isSameDay(dateObj.date, tempStartDate.value)) {
        classes.push('bg-[#4ECF84] text-white hover:bg-[#5bc49a]')
      } else if (tempEndDate.value && isSameDay(dateObj.date, tempEndDate.value)) {
        classes.push('bg-[#4ECF84] text-white hover:bg-[#5bc49a]')
      } else if (tempStartDate.value && tempEndDate.value && 
                 dateObj.date > tempStartDate.value && dateObj.date < tempEndDate.value) {
        // Keep the background white for in-between range dates
        classes.push('bg-white text-[#2F3033]')
      }

      return classes.join(' ')
    }

    const previousMonth = () => {
      const prev = new Date(currentMonth.value)
      prev.setDate(1)
      prev.setMonth(prev.getMonth() - 1)
      currentMonth.value = prev
    }

    const goToNextMonth = () => {
      const next = new Date(currentMonth.value)
      next.setDate(1)
      next.setMonth(next.getMonth() + 1)
      currentMonth.value = next
    }

    const formatMonthYear = (date) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec']
      return `${months[date.getMonth()]} ${date.getFullYear()}`
    }

    const formatDate = (date) => {
      // Format as '14 Feb' (day first, short month), no year
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short'
      })
    }

    const isSameDay = (date1, date2) => {
      return date1.getFullYear() === date2.getFullYear() &&
             date1.getMonth() === date2.getMonth() &&
             date1.getDate() === date2.getDate()
    }

    const clearSelection = () => {
      startDate.value = null
      endDate.value = null
      tempStartDate.value = null
      tempEndDate.value = null
      quickSelection.value = null
      
      emit('dateSelected', {
        startDate: null,
        endDate: null
      })
      emit('update:modelValue', {
        startDate: null,
        endDate: null
      })
    }

    // Handle escape key
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen.value) {
        closeCalendar()
      }
    }

    // Lifecycle hooks
    onMounted(() => {
      // If we have a modelValue with a start date, ensure correctly positioned in calendar
      if (props.modelValue?.startDate) {
        currentMonth.value = new Date(props.modelValue.startDate)
      }
      document.addEventListener('keydown', handleEscape)
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', () => {
          windowWidth.value = window.innerWidth
        })
      }
      // Ensure inline mode is open when mounted
      if (props.inline) {
        isOpen.value = true
      }
    })

    onUnmounted(() => {
      document.removeEventListener('keydown', handleEscape)
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', () => {
          windowWidth.value = window.innerWidth
        })
      }
    })

    // Reset calendar when resetCalendarKey changes
    const resetCalendar = () => {
      startDate.value = null
      endDate.value = null
      tempStartDate.value = null
      tempEndDate.value = null
      isOpen.value = false
      currentMonth.value = new Date()
      quickSelection.value = null
    }

    watch(() => props.resetCalendarKey, (newKey) => {
      if (newKey > 0) {
        resetCalendar()
      }
    })

    // React to inline prop changes dynamically
    watch(() => props.inline, (val) => {
      if (val) {
        isOpen.value = true
      }
    })

    // Watch for external modelValue changes
    watch(() => props.modelValue, (newVal) => {
      if (newVal) {
        startDate.value = newVal.startDate || null
        endDate.value = newVal.endDate || null
        // Also update temp values if calendar is open
        if (isOpen.value) {
          tempStartDate.value = newVal.startDate || null
          tempEndDate.value = newVal.endDate || null
        }
      } else {
        startDate.value = null
        endDate.value = null
        tempStartDate.value = null
        tempEndDate.value = null
      }
    }, { deep: true })

    return {
      isOpen,
      currentMonth,
      displayText,
      mobileDisplayText,
      isPlaceholder,
      isDesktop,
      dropdownStyle,
      currentMonthDates,
      dayHeaders,
      inputRef,
      quickSelection,
      toggleCalendar,
      closeCalendar,
      selectDate,
      applySelection,
      nextMonth,
      nextMonthDates,
      onSelectToday,
      onSelectNotSure,
      onSelectWeekend,
      todayLabel,
      weekendLabel,
      getDateClasses,
      previousMonth,
      goToNextMonth,
      formatMonthYear,
      clearSelection
    }
  }
}
</script>
